/**
 * A route test's state in a client's words (19/10/2026): the badge, the
 * one-line summary of the closed section, and what happens next.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenRouteTestToday } from '@trafficpulse/shared';

export const STATE_WORDS: Record<DropGenRouteTestToday['state'], { badge: string; tone: 'secondary' | 'info' | 'warning' | 'success' }> = {
  waiting: { badge: 'not started', tone: 'secondary' },
  sending: { badge: 'sending', tone: 'info' },
  settling: { badge: 'settling', tone: 'info' },
  ready: { badge: 'ready to pick', tone: 'warning' },
  won: { badge: 'winner picked', tone: 'success' },
};

const short = (name: string | null, id: number) => (name ?? `Gateway ${id}`).replace(/^\([^)]*\)\s*/, '').replace(/\s*\([^)]*\)\s*$/, '').trim();

/** "3 gateways tested · 12 of 20 drops sent" / "winner 23Telecom · PlayPunt, applied to 2 containers". */
export function routeTestSummary(t: DropGenRouteTestToday): string {
  if (!t.followed) return 'not followed by the group — its winner would not apply';
  if (t.winner) return `winner ${short(t.winner.routeName, t.winner.routeId)} · ${t.winner.sender}, applied to ${t.winner.appliedTo.length} container${t.winner.appliedTo.length === 1 ? '' : 's'}`;
  const tested = new Set(t.samples.map((s) => s.routeId)).size;
  if (t.drops.planned === 0) return 'no test drop planned today';
  const sends = `${t.drops.sent} of ${t.drops.planned} drops sent`;
  return tested > 0 ? `${tested} gateway${tested === 1 ? '' : 's'} tested · ${sends}` : sends;
}

/** Why a route test runs for nothing, or null. */
export function routeTestProblem(t: DropGenRouteTestToday): string | null {
  if (t.followed) return null;
  const now = t.masterStrategy ? ` — today it is “${STRATEGY_NAMES[t.masterStrategy] ?? t.masterStrategy}”` : '';
  return `The group's master config does not follow this route test${now}. Set its Gateways strategy to “Route tests” (master config › Gateways) and the winner takes the rest of the day in every container of the group.`;
}

const STRATEGY_NAMES: Record<string, string> = { HOLD_UNTIL_FAIL: 'Hold until fails', ALTERNATE: 'Alternate', RANDOM: 'Random', SPLIT: 'Split', ROUTE_TEST: 'Route tests' };

const n = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/** 20/10/2026 — a test drop auto-send gave up never sends by itself; the test waits for a person. */
function sendingNext(t: DropGenRouteTestToday): string {
  const left = t.drops.left ?? 0;
  const toGo = t.drops.planned - t.drops.sent - left;
  const parts = [
    left > 0 ? `${n(left, 'test drop')} ${left === 1 ? 'was' : 'were'} left for a person (auto-send gave it up) — send it from Today's drafts, or the test cannot finish.` : null,
    toGo > 0 ? `${n(toGo, 'test drop')} still to send. Each needs ${t.settleMin} min after its send before it counts.` : null,
  ];
  return parts.filter((p): p is string => p !== null).join(' ');
}

/** What happens next, for the open section. `readyAt` is already worded (Danish first). */
export function routeTestNext(t: DropGenRouteTestToday, readyAt: string | null): string {
  switch (t.state) {
    case 'waiting':
      return t.drops.planned === 0 ? 'Its schedule plans no test drop today.' : `Waiting for the first test drop to go out (${t.drops.planned} planned).`;
    case 'sending':
      return sendingNext(t);
    case 'settling':
      return `Every test drop is sent; the last one settles at ${readyAt ?? '—'}. Then the next verdict pass picks the winner on ${t.source === 'gateway' ? "each gateway's own numbers" : "the test drops' numbers"}.`;
    case 'ready':
      return 'Every test drop has settled — the next verdict pass picks the winner (Live tab › Evaluate verdicts, or the scheduler).';
    case 'won':
      return `The winner's gateway and sender took the rest of the day in the group's containers. ${t.recentWins.byRoute.length > 0 ? `Last ${t.recentWins.days} days: ${t.recentWins.byRoute.map((r) => `${short(r.routeName, r.routeId)} ×${r.wins}`).join(', ')}.` : ''}`.trim();
  }
}
